import React, { useState, useEffect, useMemo } from 'react'
import { Button, Expander, ExpanderHeader, ExpanderContent, Card, ResponsiveTable, H5 } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useLocation } from '@fs/zion-router'
import { getImage, getPersonName, useSlsStateContext } from '@fs/zion-sls'
import ChangeHistoryRow from './ChangeHistoryRow'
import { getPersonActionsHistory } from '../utils'

export default function PersonRecentChanges({ person, expanded }) {
  const state = useSlsStateContext()
  const [open, setOpen] = useState(false)
  const [t] = useTranslation()
  const currentImage = getImage(state?.images, state?.currentImageArk) || {}
  const location = useLocation()

  const changes = useMemo(
    () => getPersonActionsHistory({ actions: currentImage?.actions, person }),
    [currentImage?.actions, person]
  )

  useEffect(() => {
    setOpen(expanded)
  }, [expanded])

  // Construct the URL for navigation with personArk added to existing query params
  const navigationUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    searchParams.set('personArk', person.id)
    return `${location.pathname}?${searchParams.toString()}`
  }, [location.pathname, location.search, person.id])

  return (
    <Card>
      <Expander open={open} setOpen={() => setOpen(!open)}>
        <ExpanderHeader>
          <H5 supplementary>{`${getPersonName({ person })}${
            person.primary ? ` ${t('history-viewer.principal-person.label', '(Principal)')}` : ''
          }`}</H5>
        </ExpanderHeader>
        <ExpanderContent>
          <ResponsiveTable
            totalColumnCount={3}
            atSizeColumns={{
              default: {
                labels: [
                  t('history-viewer.changes-table.change.label', 'Change'),
                  t('history-viewer.changes-table.value.label', 'Value'),
                  t('history-viewer.changes-table.date.label', 'Date'),
                ],
              },
            }}
          >
            {changes.slice(0, 3).map((change) => (
              <ChangeHistoryRow key={`${change.actionId}_${change.sequenceNumber}`} change={change} />
            ))}
          </ResponsiveTable>
        </ExpanderContent>
      </Expander>
      <Button data-testid="view-all-changes-button" to={navigationUrl}>
        {t('history-viewer.view-all-changes.button', 'View All Changes')}
      </Button>
    </Card>
  )
}
